import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';

// Keep support.html public for App Store Connect. This route presents the same
// information inside the app shell so users retain its navigation.
const SupportPage: React.FC = () => (
  <div className="px-4 pb-28 pt-5 text-gray-200 font-body md:px-8 md:pb-12 md:pt-10">
    <SEO
      title="Support | Fantasy Futbol"
      description="Get help with Fantasy Futbol, account questions, and privacy requests."
      path="/support"
    />

    <main className="mx-auto max-w-2xl">
      <article className="rounded-xl border border-gray-700/60 bg-surface-card px-5 py-6 shadow-lg shadow-black/10 md:px-8 md:py-8">
        <header className="border-b border-gray-700/60 pb-5">
          <h1 className="font-heading text-2xl font-bold text-accent-yellow md:text-3xl">Support</h1>
          <p className="mt-2 text-sm leading-6 text-gray-300 md:text-base">
            Help with the app, account questions, and how to reach us.
          </p>
        </header>

        <div className="space-y-7 pt-6 text-sm leading-6 md:text-base md:leading-7">
          <section aria-labelledby="contact-support">
            <h2 id="contact-support" className="font-heading text-lg font-bold text-white">Contact us</h2>
            <p className="mt-2 text-gray-300">We usually respond within 24 to 48 hours.</p>
            <div className="mt-4 space-y-3 rounded-lg border border-gray-700/60 bg-primary/50 p-4">
              <p>
                <span className="font-semibold text-white">General support</span><br />
                <a href="mailto:support@fantasy.futbol" className="text-accent-light-blue underline decoration-accent-light-blue/50 underline-offset-2 hover:text-white">support@fantasy.futbol</a>
              </p>
              <p>
                <span className="font-semibold text-white">Privacy and data requests</span><br />
                <a href="mailto:privacy@fantasy.futbol" className="text-accent-light-blue underline decoration-accent-light-blue/50 underline-offset-2 hover:text-white">privacy@fantasy.futbol</a>
              </p>
              <p>
                <span className="font-semibold text-white">Report abusive content or users</span><br />
                <a href="mailto:support@fantasy.futbol?subject=Report%20Abuse" className="text-accent-light-blue underline decoration-accent-light-blue/50 underline-offset-2 hover:text-white">Email support with “Report Abuse”</a>
              </p>
            </div>
          </section>

          <section aria-labelledby="delete-account">
            <h2 id="delete-account" className="font-heading text-lg font-bold text-white">Delete your account</h2>
            <p className="mt-2 text-gray-300">You can delete your account at any time from inside the app:</p>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-gray-300 marker:font-semibold marker:text-accent-yellow">
              <li>Open Fantasy Futbol and sign in.</li>
              <li>Open the user menu.</li>
              <li>Tap <strong className="text-white">My Profile</strong>.</li>
              <li>Tap <strong className="text-white">Delete My Account</strong>.</li>
              <li>Confirm the deletion.</li>
            </ol>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-gray-300 marker:text-accent-yellow">
              <li>Your account is deactivated immediately. You are signed out and can no longer log in.</li>
              <li>Within 14 days, your personal information is permanently removed. If you signed in with Apple, your Apple ID is also disconnected from Fantasy Futbol.</li>
            </ul>
            <p className="mt-4 text-gray-300">League standings and contest results are retained in anonymized form so other users&apos; historical records stay intact. After the 14-day purge runs, your account cannot be restored.</p>
            <p className="mt-3 text-gray-300">
              If you cannot access the app, email{' '}
              <a href="mailto:privacy@fantasy.futbol" className="text-accent-light-blue underline decoration-accent-light-blue/50 underline-offset-2 hover:text-white">privacy@fantasy.futbol</a>{' '}
              from the address associated with your account.
            </p>
          </section>
        </div>
      </article>

      <nav aria-label="Legal and support links" className="mt-8 flex min-h-8 flex-wrap items-center justify-center gap-x-3 text-xs text-gray-500">
        <Link to="/contests" className="flex min-h-8 items-center hover:text-white">Contests</Link>
        <span aria-hidden="true">·</span>
        <Link to="/privacy" state={{ from: '/support' }} className="flex min-h-8 items-center hover:text-white">Privacy</Link>
        <span aria-hidden="true">·</span>
        <Link to="/contest-rules" className="flex min-h-8 items-center hover:text-white">Contest Rules</Link>
      </nav>
    </main>
  </div>
);

export default SupportPage;
